"use client"

import type { Control, FieldValues } from "react-hook-form"
import FormTextareaInput from "@/shared/components/form-inputs/form-textarea-input"
import { Checkbox } from "@/shared/components/ui/checkbox"
import { Label } from "@/shared/components/ui/label"
import { FormField, FormItem, FormControl } from "@/shared/components/ui/form"

const ENTER_JAMAICA_URL = "https://www.enterjamaica.gov.jm/"
const MISSIONSAFE_URL = "https://missionsafe.com/aim"
const SCROLLING_TEXTAREA_CLASS_NAME = "max-h-[72px] overflow-y-auto resize-none"

export function TravelInsuranceReminderCopy() {
    return (
        <div className="w-full space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p className="font-semibold text-foreground">Travel Insurance</p>
            <p>
                Travel insurance is a critical safeguard for working in disaster zones, as medical emergencies can quickly
                become costly without coverage. Insurance enables you to serve with greater confidence, protection and
                peace of mind, in challenging settings.
            </p>
            <p>
                We recommend:{" "}
                <a
                    href={MISSIONSAFE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary underline underline-offset-2 hover:text-primary/90"
                >
                    {MISSIONSAFE_URL}
                </a>
            </p>
        </div>
    )
}

type VolunteerTravelSupplementProps<T extends FieldValues = FieldValues> = {
    control: Control<T>
    showJamaicaSections: boolean
    /** Admin Update Volunteer dialogs: status fields only, no instructional copy */
    compact?: boolean
}

export function VolunteerTravelSupplement<T extends FieldValues>({
    control,
    showJamaicaSections,
    compact = false,
}: VolunteerTravelSupplementProps<T>) {
    if (!showJamaicaSections) return null

    if (compact) {
        return (
            <div className="w-full space-y-4 border-t border-border pt-4 mt-1">
                <p className="text-sm font-semibold text-foreground">Jamaica travel</p>
                <p className="text-xs text-muted-foreground">
                    <a
                        href={ENTER_JAMAICA_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary underline underline-offset-2"
                    >
                        C5 application
                    </a>
                    <span className="mx-1.5">·</span>
                    <span>Lodging: Georges Plain Church Lincoln, Westmoreland, Jamaica</span>
                </p>
                <FormField
                    control={control}
                    name={"jamaicaC5ApplicationComplete" as any}
                    render={({ field }) => (
                        <FormItem className="w-full flex flex-row items-center gap-3 space-y-0 rounded-md border border-border bg-muted/30 p-4">
                            <FormControl>
                                <Checkbox
                                    id="jamaica-c5-complete-compact"
                                    checked={field.value === true}
                                    onCheckedChange={(checked) => field.onChange(checked === true)}
                                />
                            </FormControl>
                            <Label htmlFor="jamaica-c5-complete-compact" className="text-sm font-medium cursor-pointer">
                                C5 application complete
                            </Label>
                        </FormItem>
                    )}
                />
                <FormTextareaInput
                    inputGroupProps={{ label: "Flight / travel details" }}
                    placeholder="Airlines, flight numbers, times, notes…"
                    name={"jamaicaTripFlightDetails" as any}
                    control={control}
                    rows={3}
                    className={SCROLLING_TEXTAREA_CLASS_NAME}
                />
            </div>
        )
    }

    return (
        <>
            <div className="w-full space-y-3 text-sm text-muted-foreground leading-relaxed border-t pt-5 mt-1">
                <p className="font-semibold text-foreground">C5 Form - Jamaica</p>
                <p>
                In order to enter Jamaica, you must complete a C5 customs and immigration form at the following website prior to arrival:{" "}
                    <a
                        href={ENTER_JAMAICA_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary underline underline-offset-2 hover:text-primary/90"
                    >
                        {ENTER_JAMAICA_URL}
                    </a>{" "}
                </p>
                <p>
                    <span className="font-medium text-foreground">Use this address for place of lodging:</span>
                    <br />
                    <br />
                    <span className="font-bold text-foreground">
                        Church Lincoln District, Grange Hill PO, Westmoreland, Jamaica
                    </span>
                    <br />
                    <span className="font-bold text-foreground">
                        Contact Person: Donovan Watson - 876 291 0913 or 876 388 8196
                    </span>
                </p>
                <p>
                Print out your confirmation email, or screengrab and print your application to show at the Jamaican border.
                    <br />
                    (This can help speed up the process of getting through border control.)
                </p>
                <p>Once you have completed the application you can return here and check off this task.</p>
            </div>

            <FormField
                control={control}
                name={"jamaicaC5ApplicationComplete" as any}
                render={({ field }) => (
                    <FormItem className="w-full flex flex-row items-center gap-3 space-y-0 rounded-md border border-border bg-muted/30 p-4">
                        <FormControl>
                            <Checkbox
                                id="jamaica-c5-complete"
                                checked={field.value === true}
                                onCheckedChange={(checked) => field.onChange(checked === true)}
                            />
                        </FormControl>
                        <Label htmlFor="jamaica-c5-complete" className="text-sm font-medium cursor-pointer">
                            Complete
                        </Label>
                    </FormItem>
                )}
            />

            <div className="w-full space-y-3 text-sm text-muted-foreground leading-relaxed border-t pt-5 mt-1">
                <p className="font-semibold text-foreground">Travel Information</p>
                <p>Once approved for the trip by Adventures Relief, please provide your travel information.</p>
                <p>
                    <span className="font-medium text-foreground">Arrival:</span> Fly into Montego Bay, Jamaica.
                </p>
                <p>
                    Airport Pickup will be 3pm, Saturday, so please be sure your flight arrives before that time.
                </p>
                <p>
                    <span className="font-medium text-foreground">Departure:</span> Dropoff will be 10am, so please be
                    sure your flight is after 12pm
                </p>
                <p>
                    If you cannot find flights within that time window, please contact your Mission Coordinator before
                    booking –{" "}
                    <a
                        href="mailto:dominique.little@adventures.org"
                        className="font-medium text-primary underline underline-offset-2"
                    >
                        dominique.little@adventures.org
                    </a>
                    .
                </p>
            </div>

            <div className="w-full">
                <FormTextareaInput
                    inputGroupProps={{ label: "Your flight and travel details" }}
                    placeholder="Airlines, flight numbers, arrival/departure times, etc."
                    name={"jamaicaTripFlightDetails" as any}
                    control={control}
                    rows={3}
                    className={SCROLLING_TEXTAREA_CLASS_NAME}
                />
            </div>
        </>
    )
}
